import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminGet, adminPost, adminPatch } from './adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { KeyRound, LockOpen, ShieldCheck, UserPlus, MessageSquare, UserX, UserCheck, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const BLANK = { name: '', mobile: '', email: '', role_id: '', org_unit_id: '' };

const MODULES = [
  ['live_tracking', 'Live tracking'], ['alerts', 'Alerts'], ['trips_passports', 'Trips / passports'],
  ['reports', 'Reports'], ['sites_devices', 'Sites and devices'], ['users', 'Users'],
];
const STATUS_BADGE = {
  ACTIVE: { bg: '#E7F2EE', fg: '#0B6B55' }, INVITED: { bg: '#FBF3DE', fg: '#7A5C13' },
  DEACTIVATED: { bg: '#EFEDE6', fg: '#6b7069' },
};

export default function AdminUsersRolesPage() {
  const [params] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(params.get('customer') || '');
  const [orgUnits, setOrgUnits] = useState([]);
  const [selectedNode, setSelectedNode] = useState('');
  const [usersData, setUsersData] = useState({ users: [], seats_used: 0, seat_limit: 25 });
  const [roles, setRoles] = useState([]);
  const [selRole, setSelRole] = useState(null);
  const [security, setSecurity] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK);
  const canSupport = ['ADMIN', 'OPS_SUPPORT'].includes(me?.role_code);   // Phase 7: ADMIN / OPS_SUPPORT write tenant users (audited)
  // one entry per template: the tenant's own clone wins over the platform template (avoids duplicate names in the selects)
  const tenantRoles = Object.values(roles.filter(r => !['ADMIN', 'OPS_HEAD', 'OPS_SUPPORT', 'OPS_ASSEMBLY', 'OPS_QC', 'SALES_HEAD', 'SALES_SCOPED_HEAD', 'SALES'].includes(r.template_code))
    .reduce((acc, r) => { const k = r.template_code || r.id; if (!acc[k] || (acc[k].is_template && !r.is_template)) acc[k] = r; return acc; }, {}));
  const contact = (u) => (u.email && !u.email.endsWith('@invite.local')) ? u.email : (u.mobile || u.email);
  const pendingToast = (res) => { if (res?.status === 'pending_approval') { toast.info('BFSI tenant: change is pending — an ADMIN confirms it under Operations → Approvals'); return true; } return false; };

  useEffect(() => {
    adminGet('/me').then(setMe).catch(() => {});
    adminGet('/customers').then(cs => {
      setCustomers(cs);
      setCustomerId(prev => prev || cs[0]?.id || '');
    }).catch(e => toast.error(e.message));
  }, []);

  const load = useCallback(() => {
    if (!customerId) return;
    adminGet(`/customers/${customerId}/org-units`).then(ous => {
      setOrgUnits(ous);
      setSelectedNode(prev => ous.find(o => o.id === prev) ? prev : (ous[0]?.id || ''));
    }).catch(() => {});
    adminGet(`/customers/${customerId}/roles`).then(rs => { setRoles(rs); setSelRole(p => rs.find(r => r.id === p?.id) || rs[0] || null); }).catch(() => {});
    adminGet(`/customers/${customerId}/security`).then(setSecurity).catch(() => {});
  }, [customerId]);

  const loadUsers = useCallback(() => {
    if (!customerId) return;
    const q = selectedNode ? `?org_unit_id=${selectedNode}` : '';
    adminGet(`/customers/${customerId}/users${q}`).then(setUsersData).catch(e => toast.error(e.message));
  }, [customerId, selectedNode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);


  // Phase 2b §4a: tenant users are managed by the customer. Staff keep exactly two actions — reset password and unlock.
  const resetPassword = async (u) => {
    if (!window.confirm(`Send a set-password link to ${u.name}?`)) return;
    setBusy(true);
    try {
      const res = await adminPost(`/customers/${customerId}/users/${u.id}/reset-password`, {});
      toast.success(res.dev_reset_url ? `Reset link issued (DEV): ${res.dev_reset_url}` : `Reset link sent via ${res.sent_via}`);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const unlockUser = async (u) => {
    setBusy(true);
    try {
      const res = await adminPost(`/customers/${customerId}/users/${u.id}/unlock`, {});
      toast.success(res.was_locked ? `${u.name} unlocked` : `${u.name} was not locked — counters cleared`);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const addUser = async () => {
    if (form.name.trim().length < 2 || (!form.mobile.trim() && !form.email.trim())) { toast.error('Name and a mobile (or email) are required'); return; }
    setBusy(true);
    try {
      const res = await adminPost(`/customers/${customerId}/users`, { name: form.name.trim(), mobile: form.mobile.trim() || null, email: form.email.trim() || null,
        role_id: form.role_id || tenantRoles[0]?.id, org_unit_id: form.org_unit_id || orgUnits[0]?.id });
      if (!pendingToast(res)) toast.success(`${form.name} added — logs in by OTP on first use${res.welcome_sms === 'LOGGED' ? ' (welcome SMS stubbed in preview)' : ''}`);
      setForm(BLANK); setAdding(false); loadUsers();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const setStatus = async (u, status) => {
    if (status === 'DEACTIVATED' && !window.confirm(`Disable ${u.name}? They lose access immediately.`)) return;
    setBusy(true);
    try { const res = await adminPatch(`/customers/${customerId}/users/${u.id}`, { status }); if (!pendingToast(res)) toast.success(status === 'DEACTIVATED' ? `${u.name} disabled` : `${u.name} re-enabled`); loadUsers(); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const changeRole = async (u, roleId) => {
    if (!roleId) return;
    setBusy(true);
    try { const res = await adminPatch(`/customers/${customerId}/users/${u.id}`, { role_id: roleId, org_unit_id: u.assignments[0]?.org_unit_id || orgUnits[0]?.id }); if (!pendingToast(res)) toast.success(`Role changed for ${u.name}`); loadUsers(); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const resendWelcome = async (u) => {
    setBusy(true);
    try { const res = await adminPost(`/customers/${customerId}/users/${u.id}/resend-welcome`, {}); toast.success(res.result === 'LOGGED' ? 'Welcome SMS logged (provider stubbed in preview)' : `Welcome SMS ${res.result}`); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const selectedName = orgUnits.find(o => o.id === selectedNode)?.name || '';
  const customer = customers.find(c => c.id === customerId);

  const HeadCard = ({ title, value, testId, hint }) => (
    <div className="rounded-lg border bg-white p-4" style={{ borderColor: '#E5E3DC' }} data-testid={testId} title={hint}>
      <p className="text-xs" style={{ color: '#6b7069' }}>{title}</p>
      <p className="text-xl font-bold mt-0.5" style={{ color: '#1B1D1C' }}>{value}</p>
      {hint && <p className="text-[11px] mt-1" style={{ color: '#9a9e96' }}>{hint}</p>}
    </div>
  );

  return (
    <div className="p-8" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div>
          <select data-testid="users-customer-select" value={customerId} onChange={e => setCustomerId(e.target.value)}
            className="h-8 px-2 rounded-md border bg-white text-sm mb-1" style={{ borderColor: '#D9D6CD' }}>
            {customers.map(c => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
          </select>
          <h1 className="text-4xl font-bold" style={{ color: '#1B1D1C' }}>Users and roles</h1>
        </div>
        {canSupport ? (
          <Button data-testid="add-tenant-user-btn" disabled={!customerId} onClick={() => setAdding(a => !a)} className="text-white hover:opacity-90" style={{ background: '#0B6B55' }}>
            <UserPlus className="w-4 h-4 mr-2" /> Add user
          </Button>
        ) : (
          <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border" style={{ borderColor: '#D9D6CD', color: '#6b7069', background: '#FAF9F6' }} data-testid="tenant-users-readonly-note">
            <ShieldCheck className="w-4 h-4" /> Read-only for your role — ADMIN and OPS_SUPPORT manage tenant users
          </div>
        )}
      </div>
      {adding && canSupport && (
        <div className="rounded-lg border bg-white p-4 mt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 items-end" style={{ borderColor: '#E5E3DC' }} data-testid="add-tenant-user-form">
          <div><label className="text-xs block mb-1" style={{ color: '#6b7069' }}>Name</label><Input data-testid="tenant-user-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white border-[#D9D6CD]" /></div>
          <div><label className="text-xs block mb-1" style={{ color: '#6b7069' }}>Mobile</label><Input data-testid="tenant-user-mobile" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} placeholder="+91…" className="bg-white border-[#D9D6CD]" /></div>
          <div><label className="text-xs block mb-1" style={{ color: '#6b7069' }}>Email (optional)</label><Input data-testid="tenant-user-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-white border-[#D9D6CD]" /></div>
          <div><label className="text-xs block mb-1" style={{ color: '#6b7069' }}>Role</label>
            <select data-testid="tenant-user-role" value={form.role_id} onChange={e => setForm({ ...form, role_id: e.target.value })} className="w-full h-9 px-2 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
              {tenantRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select></div>
          <div><label className="text-xs block mb-1" style={{ color: '#6b7069' }}>Scope</label>
            <select data-testid="tenant-user-scope" value={form.org_unit_id} onChange={e => setForm({ ...form, org_unit_id: e.target.value })} className="w-full h-9 px-2 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
              {orgUnits.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
          <div className="col-span-2 md:col-span-3 xl:col-span-1 min-w-0"><Button data-testid="tenant-user-save" disabled={busy} onClick={addUser} className="w-full text-white whitespace-normal h-auto py-2 leading-tight" style={{ background: '#0B6B55' }}>Save</Button><p className="text-[11px] mt-1 text-center" style={{ color: '#9a9e96' }}>OTP login · no password</p></div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 my-5">
        <HeadCard testId="seats-card" title="Seats used" value={`${usersData.seats_used} / ${usersData.seat_limit}`} />
        <HeadCard testId="sso-card" title="Sign-in" value={security?.sso_provider ? `SSO · ${security.sso_provider}` : 'Password or OTP (SMS / email)'} />
        <HeadCard testId="scim-card" title="Auto-provisioning (SCIM)" value={security?.scim_enabled ? 'On' : 'Off'} />
        <HeadCard testId="mfa-card" title="Two-factor required" value={security?.mfa_required ? 'All roles · not enforced yet' : 'Off'} hint="Informational — no 2FA flow exists yet, so this setting does not block any login" />
      </div>

      <div className="grid xl:grid-cols-12 gap-4 items-start">
        <div className="xl:col-span-3 space-y-4 min-w-0">
        <div className="rounded-lg border bg-white p-4 min-w-0" style={{ borderColor: '#E5E3DC' }} data-testid="org-tree-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-bold" style={{ color: '#1B1D1C' }}>Organisation</h2>
          </div>
          <div className="space-y-0.5">
            {orgUnits.map(o => {
              const depth = (o.path.match(/\//g) || []).length - 1;
              return (
                <button key={o.id} data-testid={`org-node-${o.id}`} onClick={() => setSelectedNode(o.id)}
                  className="w-full text-left px-2 py-1.5 rounded text-sm break-words"
                  style={{
                    paddingLeft: `${8 + depth * 16}px`,
                    background: selectedNode === o.id ? '#E7F2EE' : 'transparent',
                    color: o.active ? '#1B1D1C' : '#b8bdb9',
                    fontWeight: depth === 0 ? 700 : 400,
                  }}>
                  {o.level_label && depth > 0 ? `${o.level_label} · ` : ''}{o.name}{o.is_site ? ' 📍' : ''}
                </button>
              );
            })}
          </div>
          <p className="text-xs mt-4 pt-3 border-t" style={{ color: '#9a9e96', borderColor: '#EDEBE4' }}>
            Levels are set per customer. A 10-person lab can use a single level.{orgUnits.some(o => o.is_site) ? ' 📍 = site (devices allocatable).' : ''}
          </p>
        </div>

        <div className="rounded-lg border bg-white p-4 min-w-0" style={{ borderColor: '#E5E3DC' }} data-testid="role-matrix-card">
          <div className="mb-2 min-w-0">
            <select data-testid="role-select" value={selRole?.id || ''} onChange={e => setSelRole(roles.find(r => r.id === e.target.value))}
              className="h-8 px-2 rounded-md border bg-white text-xs font-bold w-full" style={{ borderColor: '#D9D6CD' }}>
              {roles.map(r => <option key={r.id} value={r.id}>{r.is_template ? `${r.name} · template` : r.name}</option>)}
            </select>
            <span className="block text-xs mt-1" style={{ color: '#9a9e96' }}>Permissions · read-only here</span>
          </div>
          <table className="w-full text-sm table-fixed">
            <colgroup><col /><col className="w-11" /><col className="w-11" /><col className="w-12" /></colgroup>
            <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
              <th className="py-2 font-medium">Module</th><th className="font-medium text-center px-1">View</th>
              <th className="font-medium text-center px-1">Edit</th><th className="font-medium text-center px-1">Appr.</th></tr></thead>
            <tbody>
              {MODULES.map(([m, label]) => {
                const p = selRole?.permissions.find(x => x.module === m) || {};
                const cell = (field) => (
                  <input type="checkbox" data-testid={`perm-${m}-${field}`} checked={!!p[field]}
                    disabled readOnly
                    className="w-4 h-4 accent-[#0B6B55]" />
                );
                return (
                  <tr key={m} className="border-b" style={{ borderColor: '#F3F1EA' }}>
                    <td className="py-2.5 break-words">{label}</td>
                    <td className="text-center">{cell('can_view')}</td><td className="text-center">{cell('can_edit')}</td><td className="text-center">{cell('can_approve')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-xs mt-3" style={{ color: '#9a9e96' }}>
            Roles and permissions are edited by the customer's own admin. Approve actions require e-signature (Phase 4).
          </p>
        </div>
        </div>

        <div className="xl:col-span-9 rounded-lg border bg-white p-4 min-w-0 overflow-x-auto" style={{ borderColor: '#E5E3DC' }} data-testid="users-list-card">
          <h2 className="text-base font-bold mb-3" style={{ color: '#1B1D1C' }}>
            Users in {selectedName || 'organisation'} and below <span className="font-normal text-sm" style={{ color: '#9a9e96' }}>· {usersData.users.length} users</span>
          </h2>
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
              <th className="py-2 font-medium">Name</th><th className="font-medium">Role</th>
              <th className="font-medium">Scope</th><th className="font-medium">Status</th><th></th></tr></thead>
            <tbody>
              {usersData.users.map(u => (
                <tr key={u.id} className="border-b" style={{ borderColor: '#F3F1EA' }} data-testid={`user-row-${u.email}`}>
                  <td className="py-2.5 pr-3 min-w-0">
                    <p className="font-medium break-words">{u.name}</p>
                    <p className="text-xs break-all" style={{ color: '#9a9e96' }}>{contact(u)}</p>
                  </td>
                  <td>
                    {canSupport ? (
                      <select data-testid={`user-role-${u.email}`} value={u.assignments[0]?.role_id || ''} disabled={busy} onChange={e => changeRole(u, e.target.value)}
                        className="h-7 px-1 rounded border bg-white text-xs max-w-[140px]" style={{ borderColor: '#D9D6CD' }}>
                        {!u.assignments[0] && <option value="">— legacy admin —</option>}
                        {tenantRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    ) : (u.assignments[0]?.role || '—')}
                  </td>
                  <td className="text-xs" style={{ color: '#6b7069' }}>{u.assignments[0]?.scope || '—'}</td>
                  <td>
                    <span className="px-2 py-0.5 rounded text-xs" style={{ background: (STATUS_BADGE[u.status] || STATUS_BADGE.ACTIVE).bg, color: (STATUS_BADGE[u.status] || STATUS_BADGE.ACTIVE).fg }}>
                      {u.status}
                    </span>
                  </td>
                  <td className="text-right whitespace-nowrap align-top py-2.5">
                    {canSupport ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`user-actions-${u.email}`} disabled={busy} className="inline-flex items-center gap-1 h-7 px-2 rounded border bg-white text-xs disabled:opacity-50" style={{ borderColor: '#D9D6CD', color: '#1B1D1C' }}>
                            Actions <ChevronDown className="w-3 h-3" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white text-sm min-w-[190px]">
                          {u.status === 'DEACTIVATED'
                            ? <DropdownMenuItem data-testid={`enable-user-${u.email}`} onSelect={() => setStatus(u, 'ACTIVE')} style={{ color: '#0B6B55' }}><UserCheck className="w-3.5 h-3.5" /> Re-enable</DropdownMenuItem>
                            : <DropdownMenuItem data-testid={`disable-user-${u.email}`} onSelect={() => setStatus(u, 'DEACTIVATED')} style={{ color: '#8C2B2B' }}><UserX className="w-3.5 h-3.5" /> Disable</DropdownMenuItem>}
                          <DropdownMenuItem data-testid={`resend-welcome-${u.email}`} onSelect={() => resendWelcome(u)}><MessageSquare className="w-3.5 h-3.5" /> Resend welcome SMS</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`reset-password-${u.email}`} onSelect={() => resetPassword(u)}><KeyRound className="w-3.5 h-3.5" /> Reset password</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`unlock-user-${u.email}`} onSelect={() => unlockUser(u)}><LockOpen className="w-3.5 h-3.5" /> Unlock</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : <span className="text-xs" style={{ color: '#b8bdb9' }}>—</span>}
                  </td>
                </tr>
              ))}
              {usersData.users.length === 0 && <tr><td colSpan={5} className="py-6 text-center" style={{ color: '#9a9e96' }}>No users in this scope yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
